"use client";

import { useState, useEffect } from "react";

interface UniversalLogoProps {
  src?: string | null;
  alt: string;
  fallbackText?: string;
  className?: string;
  brightnessFix?: boolean;
}

export function UniversalLogo({ 
  src, 
  alt, 
  className, 
  fallbackText,
  brightnessFix 
}: UniversalLogoProps) {
  const [currentSrc, setCurrentSrc] = useState<string | null | undefined>(src);
  const [isTextMode, setIsTextMode] = useState(false);

  // Sync state when the prop changes
  useEffect(() => {
    if (!src) {
      setIsTextMode(true);
      setCurrentSrc(null);
    } else {
      setIsTextMode(false);
      setCurrentSrc(src);
    }
  }, [src]);

  const handleError = () => {
    // 1. If Main Logo fails, try Google Favicon
    if (currentSrc === src && src) {
      let domain = "";
      
      try {
        // Create a URL object to safely parse the domain from the src string
        const urlObj = new URL(src);
        // Remove leading slash to get "remitter.com" from "/remitter.com"
        const path = urlObj.pathname.replace(/^\/+/, '');
        // Split by slash in case there are other path segments and take the last one
        // This handles "cdn.brandfetch.io/remitter.com" or "logo.clearbit.com/remitter.com"
        const parts = path.split('/');
        domain = parts[parts.length - 1];
        
        // Simple sanity check: if it doesn't look like a domain, throw to catch block
        if (!domain.includes('.')) throw new Error("Invalid domain extraction");
        
      } catch (e) {
        // Heuristic fallback: guess domain from alt text if URL parsing fails
        domain = `${alt.toLowerCase().replace(/[^a-z0-9]/g, '')}.com`;
      }

      // Request a high-res favicon (sz=128)
      setCurrentSrc(`https://www.google.com/s2/favicons?domain=${domain}&sz=128`);
    } else {
      // 2. If Favicon also fails, show Text
      setIsTextMode(true);
    }
  };

  const finalFallbackText = fallbackText || alt || "Client";

  // RENDER TEXT MODE
  if (isTextMode || !currentSrc) {
    return (
      <div 
        className={`${className} flex items-center justify-center overflow-hidden bg-slate-100 rounded-md`}
        title={alt}
      >
        <span className="text-slate-400 font-bold text-xs uppercase tracking-wider text-center px-1 break-all leading-tight">
          {finalFallbackText}
        </span>
      </div>
    );
  }

  // RENDER IMAGE MODE
  return (
    <img 
      src={currentSrc} 
      alt={alt} 
      onError={handleError}
      className={className}
    />
  );
}